import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, skip, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment.development';
import { DataService } from '../helpers/data.service';
import { iNotificationSettingsDto } from '../helpers/Interfaces/notification-settings';
import { iPermissionsModule } from '../helpers/Interfaces/permissions';
import { StorageService } from '../helpers/storage.service';
import { hr360Menu } from '../menu/hr360.menu';
import { AuthService } from '../modules/app/auth/auth.service';
import { EmployeeService } from '../modules/hr360/employee/employee.service';
import { CustomerService } from '../modules/lead-konnekt/customer/customer.service';
import { NotificationSettingsService } from '../modules/setup/notification-settings/notification-settings.service';
import { RolesService } from '../modules/setup/roles/roles.service';
import { SetupFormsService } from '../modules/setup/setup-forms/setup-forms.service';
import { StationService } from '../modules/setup/station/station.service';
import { WorkspaceService } from '../modules/setup/workspace/workspace.service';
import { iEmpStationsResult } from '../helpers/Interfaces/emp-stations';
import { EmployeeRedirectSettingService } from '../services/employee-redirect-setting.service';

@Component({
  selector: 'app-root',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
})
export class IndexComponent implements OnInit, OnDestroy {
  @ViewChild('authFrame') authFrame!: ElementRef<HTMLIFrameElement>;

  authUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.authUrl);

  loadApp = false;
  loading = false;
  initializing = true;
  newUser = false;

  private _unsubscribeAll = new Subject<void>();
  private _isInitialized = false;

  constructor(
    private sanitizer: DomSanitizer,
    private _employeeServ: EmployeeService,
    private _customerServ: CustomerService,
    private _stationsServ: StationService,
    private _workspaceServ: WorkspaceService,
    private _dataServ: DataService,
    private _setupFormsServ: SetupFormsService,
    private _roleServ: RolesService,
    private _router: Router,
    private _authServ: AuthService,
    private _toastrServ: ToastrService,
    private _storageServ: StorageService,
    private _notifSettingServ: NotificationSettingsService,
    private _redirectSettingServ: EmployeeRedirectSettingService,
  ) {}

  ngOnInit(): void {
    window.addEventListener('message', this._handleAuthMessage.bind(this));
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onIframeLoad(): void {
    this._setupListeners();
    this.authFrame.nativeElement.contentWindow?.postMessage(
      { action: 'GET_ALL', origin: window.origin },
      environment.authUrl,
    );
  }

  private _handleAuthMessage(event: MessageEvent): void {
    if (!event.origin.startsWith(environment.authUrl) || !event.data) return;

    switch (event.data.action) {
      case 'GET_ALL':
      case 'GET_USER':
        this._handleAuthPayload(event.data.payload);
        break;

      case 'LOGOUT':
        this._dataServ.clear();
        window.location.reload();
        break;

      case 'RELOAD':
        window.location.reload();
        break;
    }
  }

  private _handleAuthPayload(payload: any): void {
    if (!payload) {
      this._dataServ.clear();
      this.initializing = false;
      this._initialize();
      return;
    }

    const data = this._storageServ.decryptValue(payload);
    if (!data) return;

    const tokenData = data.currentUser ?? data;
    const { token, refreshToken } = tokenData ?? {};

    if (token && refreshToken) {
      const decoded: any = jwtDecode(token);
      if (decoded.Id !== this._authServ._dataServ.userValue?.id) {
        this.newUser = true;
        sessionStorage.setItem('show_workspace_expiry', 'true');
        this.loading = true;
        this._authServ._dataServ.setRefreshToken(refreshToken);

        this._authServ
          .getUserById(decoded.Id, token)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((user) => {
            this._authServ._dataServ.setToken(
              this._authServ._dataServ.tokenValue || token,
            );
            this._authServ._dataServ.setUser(user);
          });
      }
    }

    if (data.sigma) this._dataServ.setCompanyId(data.sigma);
    if (data.alpha) this._dataServ.setStationId(data.alpha);
    if (data.beta) this._dataServ.setBeta(data.beta);

    this.initializing = false;
    this._initialize();
  }

  private _initialize(): void {
    if (this._isInitialized) return;
    this._isInitialized = true;

    this._dataServ.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user) => {
        if (!user) {
          this.loadApp = true;
          return;
        }

        this.loading = true;
        const role = (user.role || '').toLowerCase();

        if (role === 'customer') {
          this._bootstrapCustomer(user);
        } else if (role === 'employee') {
          this._bootstrapEmployee(user);
        } else {
          this.loading = false;
          this.loadApp = true;
        }
      });
  }

  private _bootstrapCustomer(user: any): void {
    this._customerServ.GetCustomerByUserId(user.id).subscribe({
      next: (customer) => {
        this._dataServ.setCustomer(customer);

        this.loadApp = true;
        this.loading = false;

        if (this.newUser) {
          this._navigateCustomerAfterLogin();
          return;
        }

        const currentUrl = this._router.url ?? '';
        const preservedCustomerRoutes = [
          '/i-team/uat/customer-',
          '/app/settings',
          '/lead-konnekt/tickets',
        ];
        const isOnCustomerRoute = preservedCustomerRoutes.some((route) =>
          currentUrl.startsWith(route),
        );

        if (!isOnCustomerRoute) {
          this._router.navigateByUrl('/i-team/uat/customer-dashboard', {
            replaceUrl: true,
          });
        }
      },
      error: () => {
        this.loading = false;
        this._toastrServ.error('Failed to initialize customer session.');
        this._authServ.Logout();
      },
    });
  }

  private _navigateCustomerAfterLogin(): void {
    this.newUser = false;
    this._router.navigate(['/i-team/uat/customer-dashboard'], {
      queryParams: { login: 'true' },
      replaceUrl: true,
    });
  }

  private _bootstrapEmployee(user: any): void {
    this._employeeServ.GetEmployeeByUserId(user.id).subscribe({
      next: (employee) => {
        const pendingSubscription = ['Pending', 'Expired'];
        if (!employee.isActive) {
          this._authServ.Logout();
          return;
        }

        if (pendingSubscription.includes(employee.subscriptionStatus!)) {
          this._dataServ.setEmployee(employee);
          this.loadApp = true;
          this.loading = false;
          if (!this._router.url.startsWith('/payment')) {
            this._router.navigate(['/payment'], { replaceUrl: true });
          }
          return;
        }

        this.loading = true;

        forkJoin({
          permissions: this._fetchPermissions(employee),
          stations: this._fetchStations(employee),
        })
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe({
            next: ({ permissions, stations }) => {
              this._dataServ.setPermissions(permissions);
              this._dataServ.setEmpStations(stations);
              this._dataServ.setEmployee(employee);

              this.loadApp = true;
              this.loading = false;

              if (this.newUser) {
                this._navigateAfterLogin(user);
              }

              queueMicrotask(() => this._loadEmployeeExtras(employee, user));
            },
            error: () => {
              this._toastrServ.error('Failed to initialize employee session.');
              // this._authServ.Logout();
            },
          });
      },
      error: (err) => {
        console.clear();
        console.log(err, 'an error occurred');
        // this._authServ.Logout();
      },
    });
  }

  private _fetchPermissions(employee: any) {
    return this._roleServ.getCodesByEmpRoleId(employee.empRoleId);
  }

  private _fetchStations(employee: any) {
    return this._stationsServ.getEmpStations(employee.id);
  }

  private _loadEmployeeExtras(employee: any, user: any): void {
    this._employeeServ.GetUserLinkedAccount(user.id).subscribe((accounts) => {
      const account = accounts?.[0];
      if (account) this._dataServ.setLinkedAccount(account);
    });

    if (employee.workspaceId) {
      this._workspaceServ.getById(employee.workspaceId).subscribe({
        next: (workspace) => {
          this._dataServ.setEmployee(employee, workspace.url);

          environment.web_url = environment.production
            ? workspace.url || environment.web_url
            : environment.web_url;

          this._fetchNotificationSettings();
          this._fetchForms();
        },
        error: () => {
          this._toastrServ.error(
            'Something went wrong while fetching workspace details',
          );
        },
      });
    } else {
      this._fetchNotificationSettings();
    }
  }

  private _fetchForms(): void {
    this._setupFormsServ
      .getCachedForms()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((forms) => {
        forms.forEach((x) => {
          if (!hr360Menu.some((y) => y.id === x.id)) {
            hr360Menu.push({
              id: x.id!,
              title: x.title,
              type: 'item',
              icon: this._setupFormsServ.getIconUrl(x.iconPath!),
              url: `${environment.easy_form_url}/app/allforms?typeId=${x.id}`,
              externalUrl: true,
              role: {
                roles: ['employee'],
                right: ['E', 'E1026', 'E1000', 'E1020'],
              },
            });
          }
        });
      });
  }

  private _fetchNotificationSettings(): void {
    const isSystemadmin = this._notifSettingServ._dataServ.isSystemadmin;

    if (isSystemadmin) {
      this._dataServ.setNotificationSettings(null);
      return;
    }

    this._notifSettingServ
      .getByWorkspaceId(isSystemadmin, true)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (settings) => this._dataServ.setNotificationSettings(settings),
        error: (err) => {
          console.warn(err);
          this._toastrServ.error(
            'Something went wrong while fetching notification settings.',
          );
        },
      });
  }

  private _navigateAfterLogin(user: any): void {
    const dashboardUsers = ['IYS1', 'systemadmin'];

    const navigateTo = (target: string) => {
      if (target.startsWith('http')) {
        window.location.href = target;
      } else {
        this._router.navigate([target], {
          queryParams: { login: 'true' },
          replaceUrl: true,
        });
      }
    };

    const fallbackNavigate = () => {
      let target = '/dashboard';
      if (!dashboardUsers.includes(user?.username)) {
        if (this._dataServ.empTypeValue === 's' || this._dataServ.hasPermission('N1001')) {
          target = '/i-nest/attendance/self';
        } else {
          target = '/hr360/attendance/self';
        }
      }
      navigateTo(target);
    };

    if (dashboardUsers.includes(user?.username)) {
      fallbackNavigate();
      return;
    }

    this._redirectSettingServ
      .getByEmployee()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (setting) => {
          if (setting?.defaultRedirectCode) {
            const resolvedUrl = this._redirectSettingServ.resolveRouteByCode(
              setting.defaultRedirectCode
            );
            if (
              resolvedUrl &&
              this._dataServ.hasPermission(setting.defaultRedirectCode)
            ) {
              navigateTo(resolvedUrl);
              return;
            }
          }
          fallbackNavigate();
        },
        error: (err) => {
          console.warn('Failed to fetch redirect setting on login', err);
          fallbackNavigate();
        },
      });
  }

  private _setupListeners(): void {
    this._authServ._dataServ.companyId$
      .pipe(skip(1), takeUntil(this._unsubscribeAll))
      .subscribe((v) =>
        this.authFrame.nativeElement.contentWindow?.postMessage(
          { action: 'UPDATE_SIGMA', payload: v },
          environment.authUrl,
        ),
      );

    this._authServ._dataServ.stationId$
      .pipe(skip(1), takeUntil(this._unsubscribeAll))
      .subscribe((v) =>
        this.authFrame.nativeElement.contentWindow?.postMessage(
          { action: 'UPDATE_ALPHA', payload: v },
          environment.authUrl,
        ),
      );

    this._authServ._dataServ.beta$
      .pipe(skip(1), takeUntil(this._unsubscribeAll))
      .subscribe((v) =>
        this.authFrame.nativeElement.contentWindow?.postMessage(
          { action: 'UPDATE_BETA', payload: v },
          environment.authUrl,
        ),
      );

    this._authServ._dataServ.token$
      .pipe(skip(1), takeUntil(this._unsubscribeAll))
      .subscribe((v) =>
        this.authFrame.nativeElement.contentWindow?.postMessage(
          { action: 'UPDATE_TOKEN', payload: v },
          environment.authUrl,
        ),
      );
  }
}
